import type { GlobalDialogType } from '../global-dialogs.store.types';

import { useGlobalDialogsStore } from '../global-dialogs.store';

/**
 * Hook for checking if a global dialog is currently open.
 *
 * This hook provides a simple way to check if a dialog of a specific type is currently
 * active. It supports optional key filtering to check for specific dialog instances
 * when multiple instances of the same dialog type can exist.
 *
 * @param dialogType - The dialog type to check for
 * @param key - Optional key to check for a specific dialog instance. If provided,
 *              only returns true if a dialog with this exact key is open. If omitted,
 *              returns true if any dialog of the specified type is open.
 *
 * @returns true if a matching dialog is currently open, false otherwise
 *
 * @example
 * **Basic usage** - Checking if any dialog of a type is open:
 * ```tsx
 * const isEmailDialogOpen = useIsGlobalDialogOpen(GlobalDialogType.ShouldConnectEmail);
 *
 * if (isEmailDialogOpen) {
 *   console.log('Email connection dialog is open');
 * }
 * ```
 *
 * @example
 * **With specific key** - Checking for a specific dialog instance:
 * ```tsx
 * const isCampaignDialogOpen = useIsGlobalDialogOpen(
 *   GlobalDialogType.CampaignPhoneReachOut,
 *   'campaign-123'
 * );
 *
 * if (isCampaignDialogOpen) {
 *   console.log('Campaign 123 dialog is open');
 * }
 * ```
 *
 * @example
 * **Conditional rendering** - Showing/hiding UI based on dialog state:
 * ```tsx
 * const MyComponent = () => {
 *   const isDialogOpen = useIsGlobalDialogOpen(GlobalDialogType.CandidateOutreach);
 *
 *   return (
 *     <div>
 *       {isDialogOpen && <div>Candidate outreach dialog is active</div>}
 *       {!isDialogOpen && <button onClick={openDialog}>Open Outreach Dialog</button>}
 *     </div>
 *   );
 * };
 * ```
 *
 * @example
 * **Multiple dialog checks** - Checking for multiple dialog states:
 * ```tsx
 * const DialogStatus = () => {
 *   const isEmailOpen = useIsGlobalDialogOpen(GlobalDialogType.ShouldConnectEmail);
 *   const isCampaignOpen = useIsGlobalDialogOpen(GlobalDialogType.CampaignPhoneReachOut);
 *   const isOutreachOpen = useIsGlobalDialogOpen(GlobalDialogType.CandidateOutreach);
 *
 *   const openDialogCount = [isEmailOpen, isCampaignOpen, isOutreachOpen].filter(Boolean).length;
 *
 *   return <div>{openDialogCount} dialogs are currently open</div>;
 * };
 * ```
 *
 * @example
 * **Using with useEffect** - Reacting to dialog state changes:
 * ```tsx
 * const MyComponent = () => {
 *   const isDialogOpen = useIsGlobalDialogOpen(GlobalDialogType.CandidateOutreach);
 *
 *   useEffect(() => {
 *     if (isDialogOpen) {
 *       // Dialog opened - perform some action
 *       trackEvent('dialog_opened', { type: 'candidate_outreach' });
 *     } else {
 *       // Dialog closed - cleanup or reset state
 *       resetFormState();
 *     }
 *   }, [isDialogOpen]);
 *
 *   return <div>Dialog is {isDialogOpen ? 'open' : 'closed'}</div>;
 * };
 * ```
 *
 * @remarks
 * **Key Filtering**:
 * - When no key is provided, returns true if ANY dialog of the specified type is open
 * - When a key is provided, returns true only if a dialog with that exact key is open
 * - Keys are case-sensitive and must match exactly
 *
 * **Performance**:
 * - This hook is optimized and only re-renders when the dialog state actually changes
 * - Safe to use in render functions and effects
 * - Subscribes only to the activeDialogs array in the store
 *
 * **Use Cases**:
 * - Conditional rendering based on dialog state
 * - Preventing duplicate dialog opens
 * - Showing loading states while dialogs are active
 * - Tracking dialog usage in analytics
 *
 * @see {@link useGlobalDialog} for opening and closing dialogs
 * @see {@link useGlobalDialogKey} for getting active dialog keys
 * @see {@link useGlobalDialogData} for accessing dialog data
 */
export const useIsGlobalDialogOpen = (dialogType: GlobalDialogType, key?: string) => {
  const activeDialogs = useGlobalDialogsStore((state) => state.activeDialogs);

  return activeDialogs.some((dialog) => dialog.type === dialogType && (key ? dialog.key === key : true));
};
